import {
  Autocomplete,
  Box,
  Button,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { isFulfilled } from "@reduxjs/toolkit";
import { format } from "date-fns";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import DateRangeControl from "../../../common/components/functional/dateRangeControl/DateRangeControl";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import isEmpty from "../../../common/helpers/isEmpty";
import isEqual from "../../../common/helpers/isEqual";
import { ReactComponent as DocumentIcon } from "../../../common/icons/documentIcon.svg";
import { ReactComponent as EditIcon } from "../../../common/icons/editGray.svg";
import usePrevious from "../../../hooks/usePrevious";
import {
  Talent,
  TalentCertificate,
  TalentCertificateDocument,
  talentIsDeactivated,
} from "../../talents/talentAPI";
import {
  fetchTalent,
  getCertificateDocumentDownloadUrl,
  removeTalentCertificate,
  removeTalentCertificateDocument,
  selectTalentById,
  updateTalentAccount,
  updateTalentWorkRight,
} from "../../talents/talentsSlice";
import {
  RemoveTalentCertificateDocument,
  UpdateTalentAccount,
} from "../../talents/types/commands";
import { TalentWorkRightReason } from "../../talents/types/common";
import { AccessRightContext } from "../../user/AccessRightProvider";
import { ContentKey } from "../TalentDetails";
import {
  PASSPORT_CERT_TYPE,
  TALENT_WORK_RIGHT_REASONS,
  WORK_RIGHT_CERT_TYPE,
} from "../constants";
import DocumentStates from "./DocumentStates";
import { UnsavedChangesProvider } from "../../../contexts/UnsavedChangesContext";
import { CustomButton } from "../../../common/components/functional/customButton/CustomButton";
import { PassportInformation } from "./PassportInformation";

type Props = {
  handleOpenEditModal: (
    content: ContentKey,
    id?: string,
    fixedType?: string,
  ) => void;
};

interface TalentWorkRightFormValues {
  nationality: { id: string; name: string } | null;
  passport: TalentCertificate | null;
  reason: TalentWorkRightReason | "";
  start: Date | null;
  end: Date | null;
  workRightDocument: TalentCertificateDocument[] | [];
  previousWorkRightDocument: TalentCertificateDocument[] | [];
}

const TalentWorkRight = ({ handleOpenEditModal }: Props) => {
  const [edit, setEdit] = useState<boolean>(false);
  const [documentsToRemove, setDocumentsToRemove] = useState<
    RemoveTalentCertificateDocument[] | []
  >([]);
  const { t } = useTranslation(["workRights", "common"]);

  const translatedNationalities = useAppSelector(
    (state) => state.localized.nationality,
  );
  const nationalityOptions = Object.entries(translatedNationalities).map(
    ([id, name]) => ({ id, name }),
  );
  const { canEditTalentProfile } = useContext(AccessRightContext);

  const dispatch = useAppDispatch();
  const { talentId } = useParams();
  const talent = useAppSelector((state) =>
    selectTalentById(state, talentId!!),
  ) as Talent;

  const workRight = talent.workRight;
  const account = talent.account;
  const certificates = talent.certificates;

  const getCertificatesByType = useCallback(
    (type: string) =>
      certificates
        ? certificates
            .filter((cert) => cert.type === type)
            .sort((a, b) => {
              if (a.validTo && b.validTo) {
                if (a.validTo > b.validTo) {
                  return -1;
                } else {
                  return 1;
                }
              } else if (!a.validTo && b.validTo) {
                return -1;
              } else if (a.validTo && !b.validTo) {
                return 1;
              }
              return 0;
            })
        : [],
    [certificates],
  );

  const getDocuments = useCallback((certs: any[]) => {
    return certs
      ? (certs
          .flatMap((certificate) => certificate.documents)
          .filter(Boolean) as TalentCertificateDocument[])
      : [];
  }, []);

  const passportCertificates: TalentCertificate[] =
    getCertificatesByType(PASSPORT_CERT_TYPE);

  const passportCertificateDocuments: TalentCertificateDocument[] =
    getDocuments(passportCertificates);

  const workRightCertificates: TalentCertificate[] =
    getCertificatesByType(WORK_RIGHT_CERT_TYPE);

  const nationalityDefaultValue = useMemo(
    () =>
      account.nationality
        ? {
            id: account.nationality,
            name: translatedNationalities[account.nationality],
          }
        : null,
    [account.nationality, translatedNationalities],
  );

  const currentWorkRightDocuments = workRightCertificates[0]?.documents;
  const previousWorkRightDocuments =
    workRightCertificates.length > 1 && workRightCertificates[0]?.documents
      ? workRightCertificates[1].documents
      : null;

  const defaultValues: TalentWorkRightFormValues = useMemo(
    () => ({
      nationality: nationalityDefaultValue,
      passport: passportCertificates[0],
      reason: workRight?.reason || "",
      start: workRight?.start ? new Date(workRight?.start) : null,
      end: workRight?.end ? new Date(workRight?.end) : null,
      workRightDocument: currentWorkRightDocuments || [],
      previousWorkRightDocument: previousWorkRightDocuments || [],
    }),
    [
      currentWorkRightDocuments,
      nationalityDefaultValue,
      passportCertificates,
      previousWorkRightDocuments,
      workRight,
    ],
  );

  const methods = useForm({ mode: "onChange", defaultValues });

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    setValue,
    watch,
    formState: { errors, isDirty, dirtyFields, isSubmitSuccessful },
  } = methods;

  const prevDefaultValues = usePrevious(defaultValues);

  // reset form when async defaultValues fetched
  useEffect(() => {
    if (!isEqual(prevDefaultValues, defaultValues)) {
      reset(defaultValues);
    }
  }, [prevDefaultValues, defaultValues, reset]);

  const hasPreviousWorkRightDocument = !!watch("previousWorkRightDocument")
    .length;

  // reset isDirty after submitting
  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getValues());
      if (!!documentsToRemove.length) {
        setDocumentsToRemove([]);
      }
    }
  }, [isSubmitSuccessful, reset, getValues, documentsToRemove]);

  const onSubmit = async (data: TalentWorkRightFormValues) => {
    const nationalityChanged = Object.keys(dirtyFields).includes("nationality");
    const workRightsChanged = Object.keys(dirtyFields).some(
      (key) => key === "reason" || key === "start" || key === "end",
    );

    if (nationalityChanged) {
      const updatedAccount: UpdateTalentAccount = {
        ...account,
        address: account.address || {},
        talentId: talentId as string,
        nationality: data.nationality?.id,
      };

      // TODO: check conflict
      await dispatch(updateTalentAccount(updatedAccount));
    }

    if (workRightsChanged) {
      const formattedStartDate = data.start
        ? format(data.start, "yyyy-MM-dd")
        : "";
      const formattedEndDate = data.end ? format(data.end, "yyyy-MM-dd") : "";

      const updatedWorkRights = {
        talentId: talentId as string,
        version: (workRight?.version as number) || 0,
        reason: data.reason as TalentWorkRightReason,
        start: formattedStartDate,
        ...(formattedEndDate && { end: formattedEndDate }),
      };

      // TODO: check conflict
      await dispatch(updateTalentWorkRight(updatedWorkRights));
    }

    if (!!documentsToRemove.length) {
      documentsToRemove.forEach(
        async (document) =>
          await dispatch(removeTalentCertificateDocument(document)),
      );
      await dispatch(fetchTalent({ talentId }));
    }

    setEdit(false);
  };

  const getDocumentDownloadURL = async (pathspec: string) => {
    dispatch(
      getCertificateDocumentDownloadUrl({
        pathspec,
        talentId,
      }),
    ).then((action) => {
      if (isFulfilled(action)) {
        window.open(action.payload.url, "_blank", "noopener");
      }
    });
  };

  const onCancel = () => {
    setEdit(false);
    reset(defaultValues);
  };

  const onEdit = () => {
    if (!edit) return setEdit(!edit);
    handleSubmit(onSubmit)();
  };

  const findCertificate = (documentName: string) => {
    const foundedCertificate = certificates?.find((certificate) =>
      certificate.documents?.find((doc) => doc.filename === documentName),
    );
    return foundedCertificate;
  };

  const handleEditDocument = (document: TalentCertificateDocument | File) => {
    if (!(document instanceof File)) {
      const foundedCertificate = findCertificate(document.filename);
      handleOpenEditModal("workPermits_edit", foundedCertificate?.id);
      setEdit(false);
    }
  };

  const handleEditCertificate = (certificate: TalentCertificate) => {
    handleOpenEditModal("workPermits_edit", certificate.id);
    setEdit(false);
  };

  const handleDeleteCertificate = (certificate: TalentCertificate) => {
    const params = {
      id: certificate.id,
      version: certificate.version,
    };

    dispatch(removeTalentCertificate(params)).then((action) => {
      if (isFulfilled(action)) {
        setEdit(false);
        setValue("passport", null);
      }
    });
  };

  const formatDocumentName = (document: TalentCertificateDocument) => {
    const firstUnderlineIndex = document.filename.indexOf("_");
    const name = document.filename.slice(firstUnderlineIndex + 1);
    return name;
  };

  const workRightReasonExists = !!watch("reason");
  const validityPeriodStartDateExists = !!watch("start");
  const nationalityFin = methods.watch("nationality")?.id === "FI";
  const saveDisabled = !isEmpty(errors) || !isDirty;

  // TODO: add loading spinner for updates?
  return (
    <FormProvider {...methods}>
      <UnsavedChangesProvider isDirty={methods.formState.isDirty}>
        <FormSection
          title={t("workRights:workRight")}
          margin
          noBorder
          edit={edit}
          onEdit={onEdit}
          onCancel={onCancel}
          saveDisabled={saveDisabled}
          readOnly={!canEditTalentProfile || talentIsDeactivated(talent)}
        >
          <Grid
            item
            container
            xs={3}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("workRights:nationality")}
            </Typography>
          </Grid>
          <Grid item container xs={9} alignItems="center" gap={1}>
            <Controller
              name="nationality"
              control={control}
              rules={{
                required: { value: true, message: "" },
              }}
              render={({ field: { value, onChange } }) =>
                edit ? (
                  <Autocomplete
                    value={value}
                    onChange={(_, newValue) => onChange(newValue)}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    defaultValue={value}
                    options={nationalityOptions}
                    getOptionLabel={(option) => option.name}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        sx={{ width: "372px" }}
                        size="small"
                        error={!!errors.nationality}
                      />
                    )}
                  />
                ) : (
                  <Typography>{value?.name || ""}</Typography>
                )
              }
            />
          </Grid>
          <Grid
            item
            container
            xs={3}
            justifyContent="flex-end"
            alignItems={
              passportCertificateDocuments.length > 1 ? "flex-start" : "center"
            }
          >
            <Typography textAlign="end" variant="secondary">
              {t("workRights:passport")}
            </Typography>
          </Grid>
          <Grid item container xs={9} alignItems="center" gap={1}>
            <Box display="flex" flexDirection="column">
              <Controller
                name="passport"
                render={({ field: { onChange, value } }) => (
                  <Grid container alignItems="center">
                    {!value ? (
                      <CustomButton
                        disabled={talentIsDeactivated(talent)}
                        onClick={() =>
                          handleOpenEditModal(
                            "workPermits_add",
                            undefined,
                            PASSPORT_CERT_TYPE,
                          )
                        }
                        sx={{
                          height: "30px",
                          textTransform: "none",
                          borderRadius: "15px",
                          fontWeight: "700",
                          marginLeft: "10px",
                        }}
                        variant="outlined"
                        startIcon={<Add />}
                      >
                        {t("common:add")}
                      </CustomButton>
                    ) : (
                      <PassportInformation
                        certificate={value}
                        editMode={edit}
                        handleEdit={handleEditCertificate}
                        handleDelete={handleDeleteCertificate}
                      />
                    )}
                  </Grid>
                )}
              />
            </Box>
          </Grid>
          <Grid
            item
            container
            xs={3}
            justifyContent="flex-end"
            alignItems="center"
          >
            <Typography textAlign="end" variant="secondary">
              {t("workRights:workRightReason")}
            </Typography>
          </Grid>
          <Grid item container xs={9} alignItems="center" gap={1}>
            <Controller
              name="reason"
              control={control}
              rules={{
                required: { value: validityPeriodStartDateExists, message: "" },
              }}
              render={({ field: { value, onChange } }) =>
                edit ? (
                  <TextField
                    disabled={nationalityFin}
                    select
                    sx={{ width: "372px" }}
                    size="small"
                    value={value}
                    onChange={onChange}
                    error={!!errors.reason}
                  >
                    {TALENT_WORK_RIGHT_REASONS.map((reason) => (
                      <MenuItem key={reason} value={reason}>
                        {t(`workRights:${reason}`)}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Typography>{t(`workRights:${value}`)}</Typography>
                )
              }
            />
          </Grid>
          <DateRangeControl
            disabled={nationalityFin}
            edit={edit}
            startDateRequired={workRightReasonExists}
          />
          <Grid
            item
            container
            xs={3}
            justifyContent="flex-end"
            alignItems={
              currentWorkRightDocuments && currentWorkRightDocuments.length > 1
                ? "flex-start"
                : "center"
            }
          >
            <Typography textAlign="end" variant="secondary">
              {t("workRights:workRightDocument")}
            </Typography>
          </Grid>
          <Grid item container xs={9} alignItems="center" gap={1}>
            <Box display="flex" flexDirection="column">
              <Controller
                name="workRightDocument"
                render={({ field: { onChange, value } }) =>
                  value.map((document: TalentCertificateDocument) => (
                    <Box
                      display="flex"
                      alignItems="center"
                      gap="10px"
                      mb="0.5rem"
                    >
                      <Box
                        onClick={() =>
                          getDocumentDownloadURL(document.pathspec)
                        }
                        sx={{
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          gap: "10px",
                          marginRight: "2rem",
                        }}
                      >
                        <DocumentIcon style={{ minWidth: "22px" }} />
                        <Typography>{formatDocumentName(document)}</Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap="1rem">
                        {edit && (
                          <Button
                            sx={{
                              fontWeight: 400,
                              textTransform: "none",
                              padding: "4px",
                            }}
                            variant="text"
                            startIcon={<EditIcon />}
                            onClick={() => handleEditDocument(document)}
                          >
                            <Typography variant="secondary">
                              {t("common:edit")}
                            </Typography>
                          </Button>
                        )}
                        {/* TODO remove altogether or fix the bug where 'delete' cannot be done right after document upload */}
                        {/* {edit && (
                        <Button
                        sx={{
                          fontWeight: 400,
                          textTransform: "none",
                          padding: "4px",
                          }}
                          variant="text"
                          startIcon={<DeleteIcon sx={{ color: "#00000085" }} />}
                          onClick={() => {
                            handleSetDocumentsToRemove(document.filename);
                            onChange(
                              handleRemoveLocalDocument(
                                value,
                                document.filename
                                )
                                );
                                }}
                                >
                                <Typography variant="secondary">
                                {t("common:delete")}
                                </Typography>
                                </Button>
                                )} */}
                      </Box>
                    </Box>
                  ))
                }
              />
            </Box>
          </Grid>
          {hasPreviousWorkRightDocument && (
            <>
              <Grid
                item
                container
                xs={3}
                justifyContent="flex-end"
                alignItems={
                  watch("previousWorkRightDocument").length > 1
                    ? "flex-start"
                    : "center"
                }
              >
                <Typography textAlign="end" variant="secondary">
                  {t("workRights:previousDocument")}
                </Typography>
              </Grid>
              <Grid item container xs={9} alignItems="center" gap={1}>
                <Box display="flex" flexDirection="column">
                  <Controller
                    name="previousWorkRightDocument"
                    render={({ field: { onChange, value } }) =>
                      value.map((document: TalentCertificateDocument) => (
                        <Box
                          display="flex"
                          alignItems="center"
                          gap="10px"
                          mb="0.5rem"
                        >
                          <DocumentIcon style={{ minWidth: "22px" }} />
                          <Typography>
                            {formatDocumentName(document)}
                          </Typography>
                        </Box>
                      ))
                    }
                  />
                </Box>
              </Grid>
            </>
          )}
          <Grid
            item
            container
            xs={3}
            justifyContent="flex-end"
            alignItems="flex-start"
          >
            <Typography
              sx={{ maxWidth: "130px" }}
              textAlign="end"
              variant="secondary"
            >
              {t("workRights:documentState")}
            </Typography>
          </Grid>
          <Grid item container xs={9} alignItems="center" gap={1}>
            <Box display="flex" flexDirection="column">
              <DocumentStates
                certificates={{
                  passports: passportCertificates,
                  workRights: workRightCertificates,
                }}
              />
            </Box>
          </Grid>
        </FormSection>
      </UnsavedChangesProvider>
    </FormProvider>
  );
};

export default TalentWorkRight;
